import type { PrivateAgentChatModelSelection } from "@salesko/contracts";
import { PrivateAgentChatClientIntentSchema, type PrivateAgentChatClientIntent, type PrivateAgentChatRecovery } from "@salesko/contracts";
import type {
  PrivateAgentChatActivityKind,
  PrivateAgentConversation,
  PrivateAgentEvent,
  PrivateAgentEventReplayResponse,
  PrivateAgentMessage,
  PrivateAgentTurn
} from "@salesko/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AgentResearchApiError,
  performPrivateAgentChatIntent,
  readStoredSaleskoSession,
  SaleskoSessionChangeEvent,
  readPrivateAgentCanonicalConversation,
  listPrivateAgentChatEvents,
  privateAgentChatNotReadyReadiness,
  readPrivateAgentChatConversation,
  reconcilePrivateAgentChatTurn,
} from "../../lib/api";

export const PrivateAgentChatPollIntervalMs = 3_000;

export const privateAgentReadinessQueryKey = (agentId: string | undefined, placementRevision: string | undefined) =>
  ["private-agent-readiness", agentId, placementRevision] as const;

/**
 * The one description of the conversation read, shared by the chat hook and by
 * the detail tab that lists the entities the conversation touched. Both mount
 * the same query key, so the query cache stays the single source: neither
 * surface re-fetches on the other's behalf and neither reports its data upward
 * through an effect.
 *
 * The agent identity guard belongs to the read, not to a caller: a conversation
 * that belongs to a different Agent is an error, never a silently rendered
 * transcript.
 */
export function privateAgentConversationQueryOptions({
  agentId,
  conversationId,
  enabled
}: {
  agentId: string | undefined;
  conversationId: string | undefined;
  enabled: boolean;
}) {
  const owner = privateAgentChatOwnerKey();
  return {
    queryKey: ["private-agent-chat-conversation", agentId, owner] as const,
    queryFn: async () => {
      if (!owner || privateAgentChatOwnerKey() !== owner) throw new Error("Conversation owner changed before reading.");
      const canonical = await readPrivateAgentCanonicalConversation(agentId ?? "");
      if (!canonical.conversation) return null;
      const response = await readPrivateAgentChatConversation(canonical.conversation.conversationId, canonical.conversation.contextEpoch);
      if (response.conversation.agentId !== agentId) throw new Error("Conversation belongs to a different Agent.");
      return response;
    },
    enabled: enabled && Boolean(agentId && privateAgentChatOwnerKey()),
    retry: false as const
  };
}

export type PrivateAgentChatProgress = {
  activitySeq: number;
  kind: PrivateAgentChatActivityKind;
  occurredAt: string;
};

/**
 * What the agent on the user's computer is doing right now, as the API last
 * recorded it for this exact turn. It is a display fact only: an older turn's
 * progress never shows, and the kind is the whole recorded observation — the
 * device redacted every content field before it left the user's computer.
 */
export function privateAgentChatActiveProgress(
  events: ReadonlyArray<PrivateAgentEvent>,
  turnId: string | undefined
): PrivateAgentChatProgress | null {
  if (!turnId) return null;
  let latest: PrivateAgentChatProgress | null = null;
  for (const event of events) {
    if (event.type !== "turn.progress" || event.turnId !== turnId) continue;
    if (latest && event.activitySeq <= latest.activitySeq) continue;
    latest = { activitySeq: event.activitySeq, kind: event.kind, occurredAt: event.occurredAt };
  }
  return latest;
}

export type PrivateAgentChatReplayInvalidation = {
  conversation: boolean;
};

const NoPrivateAgentChatReplayInvalidation: PrivateAgentChatReplayInvalidation = {
  conversation: false
};

/**
 * Which durable reads a replay batch invalidates. The transcript rows live on
 * the conversation read, not in the event stream, so every lifecycle event the
 * projection has not seen before makes that read stale; a terminal receipt also
 * changes the canonical conversation summary.
 *
 * `turn.progress` invalidates neither: it changes no durable row the reads
 * return, and refetching the conversation on every activity observation would
 * put a read behind each poll for a line the replay already carries.
 */
export function privateAgentChatReplayInvalidation(
  state: PrivateAgentChatReplayState,
  conversationId: string | undefined,
  replay: PrivateAgentEventReplayResponse
): PrivateAgentChatReplayInvalidation {
  if (!conversationId || replay.conversationId !== conversationId) return NoPrivateAgentChatReplayInvalidation;
  const known = new Set(privateAgentChatReplayEvents(state, conversationId).map((event) => event.eventId));
  const fresh = replay.events.filter((event) => !known.has(event.eventId));
  return {
    conversation: fresh.some((event) => event.type === "turn.status" || event.type === "turn.terminal" || event.type === "turn.input" || event.type === "turn.settled"
      || event.type === "turn.budget_bound_falsified" || event.type === "turn.budget_integrity_mismatch_observed"
      || event.type === "conversation.cleared" || event.type === "turn.context_overflow_observed" || event.type === "turn.usage_unavailable_observed")
  };
}

export function newPrivateAgentChatClientRequestId() {
  return globalThis.crypto.randomUUID();
}

/**
 * `message` is durable user-authored transcript content. Trimming is allowed
 * only to reject an empty composer; the submitted bytes must otherwise remain
 * exactly as the user entered them.
 */
export function privateAgentChatMessageForSubmit(message: string): string | null {
  return message.trim().length > 0 ? message : null;
}

/**
 * Replay projection state, keyed by the conversation it was read from. The key
 * is part of the value so the cursor and the accumulated events are derived on
 * the same render that changes `conversationId`: a switched conversation reads
 * from sequence 0 instead of carrying the previous conversation's cursor into a
 * replay request the authority would reject as a gap.
 */
export type PrivateAgentChatReplayState = {
  conversationId: string | undefined;
  epoch: number | undefined;
  cursor: number;
  events: PrivateAgentEvent[];
};

const NoPrivateAgentChatEvents: PrivateAgentEvent[] = [];

type PrivateAgentChatReconcileVariables = {
  targetConversationId: string;
  turnId: string;
};

type PrivateAgentChatReconcileMutationSnapshot = {
  error: unknown;
  isError: boolean;
  isPending: boolean;
  variables: PrivateAgentChatReconcileVariables | undefined;
};

/**
 * A mutation observer belongs to the hook instance, not to a conversation. Do
 * not expose its status unless the variables identify the conversation and
 * active turn currently being projected.
 */
export function privateAgentChatReconcileProjection(
  mutation: PrivateAgentChatReconcileMutationSnapshot,
  conversationId: string | undefined,
  turnId: string | undefined
) {
  const variables = mutation.variables;
  const isCurrentTurn = Boolean(
    conversationId &&
      turnId &&
      variables?.targetConversationId === conversationId &&
      variables?.turnId === turnId
  );
  return {
    error: isCurrentTurn && mutation.isError ? mutation.error : null,
    isError: isCurrentTurn && mutation.isError,
    isPending: isCurrentTurn && mutation.isPending
  };
}

export function privateAgentChatReplayCursor(
  state: PrivateAgentChatReplayState,
  conversationId: string | undefined
): number {
  return state.conversationId === conversationId ? state.cursor : 0;
}

export function privateAgentChatReplayEvents(
  state: PrivateAgentChatReplayState,
  conversationId: string | undefined
): PrivateAgentEvent[] {
  return state.conversationId === conversationId ? state.events : NoPrivateAgentChatEvents;
}

/**
 * Merges one durable replay batch into the projection. A batch is only ever
 * applied to the conversation it was read from; a late batch for a conversation
 * the user already left is dropped instead of being attributed to the current
 * one.
 */
export function applyPrivateAgentChatReplay(
  state: PrivateAgentChatReplayState,
  conversationId: string | undefined,
  replay: PrivateAgentEventReplayResponse,
  epoch: number
): PrivateAgentChatReplayState {
  if (!conversationId || replay.conversationId !== conversationId || replay.epoch !== epoch) return state;
  if (state.epoch !== epoch) state = { conversationId, epoch, cursor: 0, events: [] };
  const current = privateAgentChatReplayEvents(state, conversationId);
  const known = new Set(current.map((event) => event.eventId));
  const events = [...current, ...replay.events.filter((event) => !known.has(event.eventId))].sort(
    (left, right) => left.sequence - right.sequence
  );
  const cursor = Math.max(privateAgentChatReplayCursor(state, conversationId), replay.nextSequence);
  if (state.conversationId === conversationId && cursor === state.cursor && events.length === current.length) {
    return state;
  }
  return { conversationId, epoch, cursor, events };
}

export type PrivateAgentChatState = {
  activeProgress: PrivateAgentChatProgress | null;
  activeTurn: PrivateAgentTurn | null;
  recovery: PrivateAgentChatRecovery | null;
  pendingIntent: PrivateAgentChatClientIntent | null;
  intentError: unknown;
  performing: boolean;
  intentReady: boolean;
  executeAction: (kind: "stop" | "end" | "retry", turnId: string) => Promise<boolean>;
  stopAndSend: (turnId: string, message: string, modelSelection: PrivateAgentChatModelSelection) => Promise<boolean>;
  retryPending: () => Promise<boolean>;
  conversation: PrivateAgentConversation | null;
  conversationError: unknown;
  conversationLoading: boolean;
  clearChat: () => Promise<boolean>;
  hasMoreMessages: boolean;
  loadingOlder: boolean;
  loadOlder: () => void;
  events: PrivateAgentEvent[];
  eventsError: unknown;
  messages: PrivateAgentMessage[];
  reconcileError: unknown;
  reconcileNow: (turnId?: string) => void;
  refresh: () => void;
  sendMessage: (message: string, modelSelection: PrivateAgentChatModelSelection) => Promise<boolean>;
  sending: boolean;
  turns: PrivateAgentTurn[];
};

export type UsePrivateAgentChatOptions = {
  agentId: string | undefined;
  conversationId: string | undefined;
  enabled: boolean;
};

/** Whether the selected view still has queue work or an unresolved device execution. */
export function privateAgentChatRecoveryNeedsRefresh(recovery: PrivateAgentChatRecovery): boolean {
  return recovery.unsettledTurns > 0 || recovery.executions.length > 0 || recovery.turns.some(row =>
    !["unprepared", "pending", "withdrawn_before_dispatch", "terminal"].includes(row.executionObservation));
}

export function privateAgentChatIntentStorageKey(scope: Pick<PrivateAgentChatClientIntent, "owner" | "agentId" | "conversationId">) {
  return `salesko.chatIntent.v2:${JSON.stringify([scope.owner.tenantId, scope.owner.userId, scope.agentId])}`;
}

export function readPrivateAgentChatIntent(storage: Pick<Storage, "getItem">, scope: Pick<PrivateAgentChatClientIntent, "owner" | "agentId" | "conversationId">) {
  const raw = storage.getItem(privateAgentChatIntentStorageKey(scope));
  if (raw === null) return null;
  const intent = PrivateAgentChatClientIntentSchema.parse(JSON.parse(raw));
  if (privateAgentChatIntentStorageKey(intent) !== privateAgentChatIntentStorageKey(scope)) throw new Error("Pending request belongs to a different owner or conversation.");
  return intent;
}

/** These repository verdicts exclude a commit of these exact request bytes, including on replay. */
function intentRefused(error: unknown): boolean {
  return error instanceof AgentResearchApiError && ((error.status === 404 && error.code === "not_found") || (error.status === 409 && [
    "private_agent_chat_queue_full", "private_agent_chat_request_conflict", "private_agent_chat_stop_send_conflict",
    "context_epoch_conflict", "subscription_lane_unsupported", "context_budget_unconfigured", "queue_full", "conflict", "private_agent_chat_cancel_conflict", "private_agent_chat_end_conflict", "private_agent_chat_retry_conflict", "private_agent_chat_retry_not_ready"
  ].includes(error.code ?? "")));
}

export function privateAgentChatOwnerKey() {
  const session = readStoredSaleskoSession();
  return session ? JSON.stringify([session.tenantId, session.userId]) : null;
}

export function usePrivateAgentChat({ agentId, enabled }: UsePrivateAgentChatOptions): PrivateAgentChatState {
  const queryClient = useQueryClient();
  const [, sessionChanged] = useState(0);
  useEffect(() => {
    const changed = () => sessionChanged(value => value + 1);
    window.addEventListener(SaleskoSessionChangeEvent, changed);
    window.addEventListener("storage", changed);
    return () => { window.removeEventListener(SaleskoSessionChangeEvent, changed); window.removeEventListener("storage", changed); };
  }, []);
  const session = readStoredSaleskoSession();
  const ownerKey = session ? JSON.stringify([session.tenantId, session.userId]) : null;
  const authorized = enabled && ownerKey !== null && Boolean(agentId);
  const conversationQuery = useQuery({
    ...privateAgentConversationQueryOptions({ agentId, conversationId: undefined, enabled: authorized }),
    refetchInterval: query => query.state.data && privateAgentChatRecoveryNeedsRefresh(query.state.data.recovery) ? PrivateAgentChatPollIntervalMs : false
  });
  const data = authorized ? conversationQuery.data : undefined;
  const conversationId = data?.conversation.conversationId;
  const epoch = data?.contextEpoch;
  const scope = session && agentId ? { owner: { tenantId: session.tenantId, userId: session.userId }, agentId, conversationId: conversationId ?? null } : null;
  const scopeKey = authorized && scope ? privateAgentChatIntentStorageKey(scope) : null;
  const [intentState, setIntentState] = useState<{ key: string | null; intent: PrivateAgentChatClientIntent | null; error: unknown; ready: boolean }>({ key: null, intent: null, error: null, ready: false });
  const intentRef = useRef(intentState);
  const inFlight = useRef(new Set<string>());
  const [busyKeys, setBusyKeys] = useState(new Set<string>());
  const updateIntent = useCallback((state: typeof intentState) => { intentRef.current = state; setIntentState(state); }, []);
  const loadIntent = useCallback(() => {
    if (!scopeKey || !scope) { updateIntent({ key: scopeKey, intent: null, error: null, ready: false }); return; }
    try { updateIntent({ key: scopeKey, intent: readPrivateAgentChatIntent(window.sessionStorage, scope), error: null, ready: true }); }
    catch (error) { updateIntent({ key: scopeKey, intent: null, error, ready: false }); }
    // Scope is entirely represented by this key; changes never retarget a stored request.
  }, [scopeKey, updateIntent]);
  useEffect(loadIntent, [loadIntent]);
  const pendingIntent = intentState.key === scopeKey ? intentState.intent : null;
  const intentError = intentState.key === scopeKey ? intentState.error : null;
  const performing = scopeKey !== null && busyKeys.has(scopeKey);
  const intentReady = scopeKey !== null && intentState.key === scopeKey && intentState.ready;

  const [replayState, setReplayState] = useState<PrivateAgentChatReplayState>({ conversationId, epoch, cursor: 0, events: [] });
  const replayScope = `${ownerKey}:${agentId}:${conversationId}:${epoch}`;
  const [readReplayScope, setReadReplayScope] = useState(replayScope);
  const events = authorized && readReplayScope === replayScope ? privateAgentChatReplayEvents(replayState, conversationId) : NoPrivateAgentChatEvents;
  const eventCursor = readReplayScope === replayScope ? privateAgentChatReplayCursor(replayState, conversationId) : 0;
  const recovery = data?.recovery ?? null;
  const turns = data?.turns ?? [];
  const recoveryRows = [...(recovery?.turns ?? []), ...(recovery?.executions ?? [])];
  const activeRow = recoveryRows.filter(row => !["unprepared", "pending", "withdrawn_before_dispatch", "terminal"].includes(row.executionObservation))
    .sort((left, right) => left.turnSequence - right.turnSequence)[0]
    ?? recovery?.turns.find(row => row.turnId === recovery.headTurnId);
  const activeTurn = turns.find(turn => turn.turnId === activeRow?.turnId) ?? null;
  // Progress lacks generation identity. It cannot describe a later Retry generation.
  const activeProgress = activeTurn?.execution?.generation === 1 && activeRow?.settlement === null
    ? privateAgentChatActiveProgress(events, activeTurn.turnId) : null;
  const eventsQuery = useQuery({
    queryKey: ["private-agent-chat-events", conversationId, epoch, eventCursor, agentId, ownerKey],
    queryFn: () => {
      if (!ownerKey || privateAgentChatOwnerKey() !== ownerKey) throw new Error("Conversation owner changed before reading.");
      return listPrivateAgentChatEvents(conversationId ?? "", eventCursor, epoch!);
    },
    enabled: authorized && Boolean(conversationId) && !conversationQuery.isError, retry: false,
    refetchInterval: recovery && privateAgentChatRecoveryNeedsRefresh(recovery) ? PrivateAgentChatPollIntervalMs : false,
    refetchIntervalInBackground: false
  });
  useEffect(() => {
    const replay = eventsQuery.data;
    if (!authorized || !replay || replay.epoch !== epoch || replay.conversationId !== conversationId) return;
    const current = readReplayScope === replayScope ? replayState : { conversationId, epoch, cursor: 0, events: [] };
    const invalidation = privateAgentChatReplayInvalidation(current, conversationId, replay);
    setReadReplayScope(replayScope);
    setReplayState(applyPrivateAgentChatReplay(current, conversationId, replay, epoch!));
    if (invalidation.conversation) void queryClient.invalidateQueries({ queryKey: ["private-agent-chat-conversation", agentId, ownerKey] });
  }, [authorized, conversationId, epoch, agentId, ownerKey, eventsQuery.data, queryClient]);

  // Epoch conflict/cursor expiry invalidates this read; only a fresh Host snapshot may reset it.
  useEffect(() => {
    const error = eventsQuery.error;
    if (!(error instanceof AgentResearchApiError) || !["context_epoch_conflict", "cursor_expired"].includes(error.code ?? "")) return;
    void conversationQuery.refetch();
    if (error.code === "cursor_expired") {
      setReplayState({conversationId, epoch, cursor: data?.eventWatermark ?? 0, events: []});
    }
  }, [eventsQuery.error]);

  const invalidate = useCallback((target: Pick<PrivateAgentChatClientIntent, "owner" | "agentId" | "conversationId">) => {
    const owner = JSON.stringify([target.owner.tenantId, target.owner.userId]);
    void queryClient.invalidateQueries({ queryKey: ["private-agent-chat-conversation", target.agentId, owner] });
    void queryClient.invalidateQueries({ queryKey: ["private-agent-chat-events", target.conversationId] });
  }, [queryClient]);
  const onNotReady = useCallback((error: unknown) => {
    const readiness = privateAgentChatNotReadyReadiness(error);
    if (!readiness) return;
    const key = privateAgentReadinessQueryKey(readiness.agentId, readiness.placementRevision);
    queryClient.setQueryData(key, readiness);
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ["private-agent-profile", readiness.agentId] });
  }, [queryClient]);
  const executeIntent = useCallback(async (candidate: PrivateAgentChatClientIntent, replay: boolean) => {
    if (!authorized || !scope || !scopeKey || !intentReady || inFlight.current.has(scopeKey)
      || data === undefined || conversationQuery.isError || (data !== null && data.conversation.agentId !== agentId) || privateAgentChatOwnerKey() !== ownerKey) return false;
    const intent = PrivateAgentChatClientIntentSchema.parse(candidate);
    if (privateAgentChatIntentStorageKey(intent) !== scopeKey) return false;
    const current = intentRef.current;
    if (current.key !== scopeKey || (replay ? current.intent !== candidate : current.intent !== null)) return false;
    inFlight.current.add(scopeKey); setBusyKeys(new Set(inFlight.current));
    let persisted = false;
    try {
      const storage = window.sessionStorage;
      if (!replay) {
        if (storage.getItem(scopeKey) !== null) throw new Error("An unresolved request already exists. Reload its identity.");
        storage.setItem(scopeKey, JSON.stringify(intent));
        updateIntent({ key: scopeKey, intent, error: null, ready: true });
      }
      if (storage.getItem(scopeKey) !== JSON.stringify(intent)) throw new Error("Pending request is not durably saved. Reload before retrying.");
      persisted = true;
      await performPrivateAgentChatIntent(intent);
      if (storage.getItem(scopeKey) !== JSON.stringify(intent)) throw new Error("Pending request changed while waiting for its response.");
      storage.removeItem(scopeKey);
      if (intentRef.current.key === scopeKey) updateIntent({ key: scopeKey, intent: null, error: null, ready: true });
      invalidate(intent);
      return true;
    } catch (error) {
      if (privateAgentChatOwnerKey() === ownerKey) onNotReady(error);
      let unresolved: PrivateAgentChatClientIntent | null = persisted ? intent : null;
      if (persisted && intentRefused(error)) {
        try {
          if (window.sessionStorage.getItem(scopeKey) === JSON.stringify(intent)) {
            window.sessionStorage.removeItem(scopeKey);
            unresolved = null;
          }
        } catch { /* Preserve the original request when local cleanup cannot be confirmed. */ }
      }
      if (intentRef.current.key === scopeKey) updateIntent({ key: scopeKey, intent: unresolved, error, ready: persisted });
      invalidate(intent);
      return false;
    } finally {
      inFlight.current.delete(scopeKey); setBusyKeys(new Set(inFlight.current));
    }
  }, [authorized, scopeKey, intentReady, data, conversationQuery.isError, agentId, ownerKey, invalidate, onNotReady, updateIntent]);

  const sendMessage = useCallback(async (message: string, modelSelection: PrivateAgentChatModelSelection) => {
    const body = privateAgentChatMessageForSubmit(message);
    if (!scope || !body || pendingIntent) return false;
    try { return await executeIntent(PrivateAgentChatClientIntentSchema.parse({ ...scope, schemaVersion: "salesko.private_agent_chat_intent.v2", kind: "send",
      request: { clientRequestId: newPrivateAgentChatClientRequestId(), expectedContext: data ? {state: "present", conversationId, epoch} : {state: "absent"}, message: body, modelSelection } }), false); }
    catch (error) { if (scopeKey) updateIntent({ key: scopeKey, intent: null, error, ready: true }); return false; }
  }, [scopeKey, conversationId, epoch, data, pendingIntent, executeIntent, updateIntent]);
  const executeAction = useCallback(async (kind: "stop" | "end" | "retry", turnId: string) => {
    const row = [...(recovery?.turns ?? []), ...(recovery?.executions ?? [])].find(turn => turn.turnId === turnId);
    if (!scope?.conversationId || !epoch || !row || !row.actions[kind] || pendingIntent) return false;
    const request = kind === "stop" ? { expectedEpoch: row.contextEpoch, execution: row.execution } : { expectedEpoch: epoch, taskId: row.execution?.taskId ?? null, generation: row.execution?.generation ?? null, actionId: newPrivateAgentChatClientRequestId() };
    try { return await executeIntent(PrivateAgentChatClientIntentSchema.parse({ ...scope, schemaVersion: "salesko.private_agent_chat_intent.v2", kind, turnId, request }), false); }
    catch (error) { if (scopeKey) updateIntent({ key: scopeKey, intent: null, error, ready: true }); return false; }
  }, [scopeKey, conversationId, epoch, recovery, pendingIntent, executeIntent, updateIntent]);
  const stopAndSend = useCallback(async (turnId: string, message: string, modelSelection: PrivateAgentChatModelSelection) => {
    const row = [...(recovery?.turns ?? []), ...(recovery?.executions ?? [])].find(turn => turn.turnId === turnId), body = privateAgentChatMessageForSubmit(message);
    if (!scope?.conversationId || !epoch || !row?.actions.stopAndSend || !body || pendingIntent) return false;
    try { return await executeIntent(PrivateAgentChatClientIntentSchema.parse({ ...scope, schemaVersion: "salesko.private_agent_chat_intent.v2", kind: "stopAndSend",
      request: { expectedEpoch: epoch, actionId: newPrivateAgentChatClientRequestId(), target: { turnId, execution: row.execution }, input: { clientRequestId: newPrivateAgentChatClientRequestId(), expectedContext: {state: "present", conversationId, epoch}, message: body, modelSelection } } }), false); }
    catch (error) { if (scopeKey) updateIntent({ key: scopeKey, intent: null, error, ready: true }); return false; }
  }, [scopeKey, conversationId, epoch, recovery, pendingIntent, executeIntent, updateIntent]);
  const retryPending = useCallback(() => pendingIntent ? executeIntent(pendingIntent, true) : Promise.resolve(false), [pendingIntent, executeIntent]);

  const clearChat = useCallback(async () => {
    if (!scope?.conversationId || !epoch || pendingIntent) return false;
    return executeIntent({ ...scope, conversationId: scope.conversationId, schemaVersion: "salesko.private_agent_chat_intent.v2", kind: "clear",
      request: {kind: "clear_chat", expectedEpoch: epoch, clientRequestId: newPrivateAgentChatClientRequestId()} }, false);
  }, [scopeKey, conversationId, epoch, pendingIntent, executeIntent]);
  const [older, setOlder] = useState<{scope: string; messages: PrivateAgentMessage[]; before: number | null; hasMore: boolean} | null>(null);
  const olderMutation = useMutation({
    mutationFn: ({id, epoch, before}: {id: string; epoch: number; before: number; scope: string}) => readPrivateAgentChatConversation(id, epoch, before),
    onSuccess: (page, target) => setOlder(previous => ({scope: target.scope, before: page.beforeSequence, hasMore: page.hasMore,
      messages: [...page.messages, ...(previous?.scope === target.scope ? previous.messages : [])]}))
  });
  const olderPage = older?.scope === replayScope ? older : null;
  const loadOlder = () => {
    const before = olderPage ? olderPage.before : data?.beforeSequence;
    if (!conversationId || !epoch || !before || olderMutation.isPending) return;
    olderMutation.mutate({id: conversationId, epoch, before, scope: replayScope});
  };
  const reconcileMutation = useMutation({
    mutationFn: ({ targetConversationId, turnId }: PrivateAgentChatReconcileVariables & { scope: NonNullable<typeof scope> }) => reconcilePrivateAgentChatTurn(targetConversationId, turnId),
    onSuccess: (_response, variables) => invalidate(variables.scope)
  });
  const reconcileProjection = privateAgentChatReconcileProjection(reconcileMutation, conversationId, reconcileMutation.variables?.turnId);
  const reconcileNow = useCallback((turnId?: string) => {
    const targetId = turnId ?? activeTurn?.turnId;
    if (!authorized || !scope || privateAgentChatOwnerKey() !== ownerKey || !conversationId || !targetId || ![...(recovery?.turns ?? []), ...(recovery?.executions ?? [])].some(turn => turn.turnId === targetId && turn.execution !== null) || reconcileProjection.isPending) return;
    reconcileMutation.mutate({ targetConversationId: conversationId, turnId: targetId, scope });
  }, [authorized, scopeKey, ownerKey, conversationId, activeTurn, recovery, reconcileMutation, reconcileProjection.isPending]);
  const refresh = useCallback(() => {
    if (!authorized) return;
    if (!performing) loadIntent();
    void conversationQuery.refetch();
    if (conversationId) void eventsQuery.refetch();
  }, [authorized, performing, loadIntent, conversationId, conversationQuery, eventsQuery]);

  return {
    activeProgress, activeTurn, recovery, pendingIntent, intentError, performing, intentReady,
    executeAction, stopAndSend, retryPending,
    conversation: data?.conversation ?? null, conversationError: authorized ? conversationQuery.error ?? (olderMutation.variables?.scope === replayScope ? olderMutation.error : null) : null,
    conversationLoading: authorized && conversationQuery.isLoading,
    clearChat, loadOlder, hasMoreMessages: olderPage?.hasMore ?? data?.hasMore ?? false, loadingOlder: olderMutation.isPending,
    events, eventsError: authorized && eventsQuery.isError ? eventsQuery.error : null,
    messages: [...new Map([...(olderPage?.messages ?? []), ...(data?.messages ?? [])].map(message => [message.messageId, message])).values()].sort((a,b) => a.sequence-b.sequence), reconcileError: authorized && reconcileMutation.variables && privateAgentChatIntentStorageKey(reconcileMutation.variables.scope) === scopeKey ? reconcileProjection.error : null,
    reconcileNow, refresh, sendMessage, sending: performing && (pendingIntent?.kind === "send" || pendingIntent?.kind === "stopAndSend"), turns
  };
}
